import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { authService } from "../services/authService";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { Input } from "./Input";
import { Button } from "./Button";

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEscapeKey(isOpen && !loading, onClose);

    // Start from a clean form each time, so a previous failure is not still on
    // screen when the modal is reopened.
    useEffect(() => {
        if (isOpen) {
            setUsername("");
            setPassword("");
            setError("");
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            await authService.login(username, password);
            onClose();
        } catch (err: any) {
            setError("Login failed. Please check your credentials.");
        } finally {
            setLoading(false);
        }
    };

    // Portalled to the body: this renders inside the navbar, which is a flex
    // item with z-10 and therefore its own stacking context, so the modal would
    // otherwise be trapped below page content with a higher z-index.
    return createPortal(
        <div className="modal modal-open" role="dialog">
            <div className="modal-box relative">
                <button
                    className="btn btn-sm btn-circle absolute right-2 top-2"
                    onClick={onClose}
                >
                    ✕
                </button>
                <h3 className="font-bold text-lg mb-4">Login</h3>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <Input
                        label="Username"
                        placeholder="Enter username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                    <Input
                        label="Password"
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />

                    {error && <div className="text-error text-sm">{error}</div>}

                    <div className="modal-action">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="btn-primary"
                            isLoading={loading}
                        >
                            Login
                        </Button>
                    </div>
                </form>
            </div>
            <button
                type="button"
                className="modal-backdrop"
                onClick={onClose}
                disabled={loading}
                aria-label="Close"
            />
        </div>,
        document.body
    );
}
